/**
 * Horizontal Scroll Module
 * Handles sticky horizontal scrolling sections with parallax effect
 * 
 * Usage:
 * <div class="horizontal-scroll-section" data-scroll-distance="auto">
 *   <div class="horizontal-scroll-sticky">
 *     <div class="horizontal-scroll-content">
 *       <!-- Your scrollable content here -->
 *     </div>
 *   </div>
 * </div>
 */

class HorizontalScroll {
  constructor(selector = '.horizontal-scroll-section') {
    this.sections = document.querySelectorAll(selector);
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.loadHandler = null;
    
    if (this.sections.length > 0) {
      this.init();
    }
  }
  
  init() {
    this.cleanup();
    this.calculateDistances();
    
    // Store bound handlers
    this.scrollHandler = this.handleScroll.bind(this);
    this.resizeHandler = () => {
      this.calculateDistances();
      this.handleScroll();
    };
    this.loadHandler = () => {
      this.calculateDistances();
      this.handleScroll();
    };
    
    // Add event listeners
    window.addEventListener('scroll', this.scrollHandler);
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('load', this.loadHandler);
  }
  
  calculateDistances() {
    this.sections.forEach(section => {
      const sticky = section.querySelector('.horizontal-scroll-sticky');
      const scrollContent = section.querySelector('.horizontal-scroll-content');
      
      if (!sticky || !scrollContent) return;
      
      const scrollWidth = scrollContent.scrollWidth;
      const viewportWidth = sticky.offsetWidth;
      const scrollDistance = scrollWidth - viewportWidth;
      
      // Get optional scroll multiplier (default 1)
      const multiplier = parseFloat(section.dataset.scrollMultiplier) || 1;
      
      // Store as data attribute
      section.dataset.scrollDistance = scrollDistance;
      
      // Set section height to allow scrolling (with optional multiplier)
      section.style.height = `${(scrollDistance * multiplier) + window.innerHeight}px`;
    });
  }
  
  handleScroll() {
    this.sections.forEach(section => {
      const sticky = section.querySelector('.horizontal-scroll-sticky');
      const scrollContent = section.querySelector('.horizontal-scroll-content');
      const scrollDistance = Number(section.dataset.scrollDistance);
      
      if (!sticky || !scrollContent || !scrollDistance) return;
      
      // Get section position
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate scroll progress
      const sectionTop = rect.top;
      const triggerPoint = viewportHeight - sticky.offsetHeight;
      const progress = (triggerPoint - sectionTop) / scrollDistance;
      const scrollPercentage = Math.max(0, Math.min(1, progress));
      
      // Apply transform
      scrollContent.style.transform = `translate3d(${-scrollPercentage * scrollDistance}px, 0, 0)`;
    });
  }
  
  cleanup() {
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    if (this.loadHandler) window.removeEventListener('load', this.loadHandler);
  }
  
  destroy() {
    this.cleanup();
    this.sections = null;
  }
}

// Auto-initialize on load
if (typeof window !== 'undefined') {
  let horizontalScrollInstance = null;
  
  function initHorizontalScroll() {
    if (horizontalScrollInstance) {
      horizontalScrollInstance.destroy();
    }
    horizontalScrollInstance = new HorizontalScroll();
  }
  
  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHorizontalScroll);
  } else {
    initHorizontalScroll();
  }
  
  // Reinitialize in Shopify design mode
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initHorizontalScroll);
    document.addEventListener('shopify:section:reorder', initHorizontalScroll);
  }
}

// Export for manual usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HorizontalScroll;
}
